// Guest chat - the guest's side (docs/GUEST_CHAT_2026-10-28.md).
// A conversation is reached only with its token (X-Chat-Token), handed out
// once when the chat starts and kept in this browser's localStorage, one per
// restaurant, so a reload resumes the chat. Nothing here is a sign-in.
import { API } from '@/config/api';

const KEY = (restaurantId) => `c29_chat:${restaurantId}`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

export class ChatError extends Error {
  constructor(status, body) {
    const detail = body?.detail;
    const message = typeof detail === 'string' ? detail
      : (detail && typeof detail.message === 'string' ? detail.message
        : (Array.isArray(detail) ? detail.map((d) => d?.msg).filter(Boolean).join('; ') : null))
      || (status === 0 ? 'Could not reach the restaurant. Check your connection and try again.' : `Something went wrong (${status}).`);
    super(message);
    this.status = status;
    this.code = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail.code || null : null;
  }
}

export function savedChat(restaurantId) {
  if (!restaurantId) return null;
  try {
    const raw = localStorage.getItem(KEY(restaurantId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed.token === 'string' ? parsed : null;
  } catch (e) {
    return null;
  }
}

export function saveChat(restaurantId, value) {
  try {
    if (value) localStorage.setItem(KEY(restaurantId), JSON.stringify(value));
    else localStorage.removeItem(KEY(restaurantId));
  } catch (e) {
    // private mode: the chat lasts while the page is open
  }
}

async function call(method, path, { body, token, query } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['X-Chat-Token'] = token;
  const qs = query ? `?${new URLSearchParams(query).toString()}` : '';
  let response;
  try {
    response = await fetch(`${API}/public/chat${path}${qs}`, {
      method, headers, body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new ChatError(0, null);
  }
  let data = null;
  try { data = await response.json(); } catch (e) { data = null; }
  if (!response.ok) throw new ChatError(response.status, data);
  return data;
}

async function captchaToken() {
  if (!RECAPTCHA_SITE_KEY || typeof window === 'undefined' || !window.grecaptcha?.enterprise) return null;
  try {
    await new Promise((resolve) => window.grecaptcha.enterprise.ready(resolve));
    return await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'CHAT' });
  } catch (e) {
    return null;
  }
}

export const guestChat = {
  findRestaurants: (q) => call('GET', '/restaurants', { query: { q } }),
  status: (restaurantId) => call('GET', `/restaurants/${restaurantId}`),
  async start(body) {
    const recaptcha_token = await captchaToken();
    return call('POST', '/conversations', { body: { ...body, recaptcha_token } });
  },
  read: (token, after = 0) => call('GET', '/conversation', { token, query: { after } }),
  send: (token, body) => call('POST', '/conversation/messages', { token, body }),
};

export function newClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// Any page can open the chat (e.g. the landing page's "Find a table"):
//   openGuestChat({ reservation: { party_size, date, time } })
export const OPEN_EVENT = 'c29:guest-chat-open';
export function openGuestChat(detail = {}) {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail }));
}
